import {
  Component,
  EventEmitter,
  OnInit,
  Input,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  ValidatorFn,
  ValidationErrors,
  AbstractControl,
  UntypedFormControl,
} from '@angular/forms';
import {
  FullRecordOptions,
  FullRecordTab,
  OpacSettingsService,
} from '../../../../services/opac-settings/opac-settings.service';
import { SharedService } from '../../../../services/shared/shared.service';

export function selectValidator(formControlOptions: any): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const staffControl = control.get('staffDefaultTabId') as UntypedFormControl;
    const patronGuestControl = control.get('patronGuestDefaultTabId') as UntypedFormControl;
    const error = {
      staffSelectDropdownError: '',
      patronGuestSelectDropdownError: '',
    };
    for (const option of formControlOptions) {
      const formControlName = option.fullRecordFormControl;
      const formControl = control.get(formControlName) as UntypedFormControl;
      const isChecked = formControl?.value;
      if (!isChecked) {
        const fullRecordOption = option.fullRecordOption as FullRecordTab;
        const fullRecordTabId = fullRecordOption.id;
        const errorMessage = `'${fullRecordOption.name}' section is disabled. Enable it below or change the default section.`;
        if (fullRecordTabId === staffControl.value && formControlName.includes('staff')) {
          error.staffSelectDropdownError = errorMessage;
        } else if (fullRecordTabId === patronGuestControl.value) {
          error.patronGuestSelectDropdownError = errorMessage;
        }
      }
    }
    if (
      SharedService.isEmpty(error.staffSelectDropdownError) &&
      SharedService.isEmpty(error.patronGuestSelectDropdownError)
    ) {
      return null;
    } else {
      return error;
    }
  };
}

@Component({
  selector: 'ag-side-nav-full-record',
  templateUrl: './side-nav-full-record.component.html',
  styleUrls: ['./side-nav-full-record.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SideNavFullRecordComponent implements OnInit {
  @Input() fullRecordOptions: FullRecordOptions;
  @Input() canShowILLLenderColumn: boolean;
  @Output() updateFullRecord = new EventEmitter<FullRecordOptions>();

  tabs: FullRecordTab[];
  fullRecordForm: UntypedFormGroup;

  constructor(
    private _opacSettingsService: OpacSettingsService,
    private _formBuilder: UntypedFormBuilder,
    private _sharedService: SharedService,
  ) {}

  ngOnInit(): void {
    // Build FormGroup
    this._initFullRecordForm();
    // Enabled Tabs
    this.tabs = [
      this.fullRecordOptions.detailTab,
      this.fullRecordOptions.whereTab,
      this.fullRecordOptions.marcTab,
    ];
    if (this.fullRecordOptions.displayMoreAboutThisTitle) {
      this.tabs.push(this.fullRecordOptions.moreAboutThisTitleTab);
    }
    if (this.fullRecordOptions.displayGoodreadsReview) {
      this.tabs.push(this.fullRecordOptions.goodreadsReviewTab);
    }
    if (this.fullRecordOptions.displaySyndeticsUnbound) {
      this.tabs.push(this.fullRecordOptions.syndeticsUnboundTab);
    }
    if (this.fullRecordOptions.displayNovelist) {
      this.tabs.push(this.fullRecordOptions.novelistTab);
    }
    if (this.fullRecordOptions.displayCLCD) {
      this.tabs.push(this.fullRecordOptions.clcdTab);
    }
    if (this.fullRecordOptions.displayShelfBrowse) {
      this.tabs.push(this.fullRecordOptions.shelfBrowseTab);
    }
  }

  private _initFullRecordForm(): void {
    let formControlOptions = [
      {
        fullRecordFormControl: 'staffDisplayMARC',
        fullRecordOption: this.fullRecordOptions['marcTab'],
      },
      {
        fullRecordFormControl: 'patronGuestDisplayMARC',
        fullRecordOption: this.fullRecordOptions['marcTab'],
      },
      {
        fullRecordFormControl: 'staffDisplayMoreAboutThisTitle',
        fullRecordOption: this.fullRecordOptions['moreAboutThisTitleTab'],
      },
      {
        fullRecordFormControl: 'patronGuestDisplayMoreAboutThisTitle',
        fullRecordOption: this.fullRecordOptions['moreAboutThisTitleTab'],
      },
      {
        fullRecordFormControl: 'staffDisplaySyndeticsUnbound',
        fullRecordOption: this.fullRecordOptions['syndeticsUnboundTab'],
      },
      {
        fullRecordFormControl: 'patronGuestDisplaySyndeticsUnbound',
        fullRecordOption: this.fullRecordOptions['syndeticsUnboundTab'],
      },
      {
        fullRecordFormControl: 'staffDisplayNovelist',
        fullRecordOption: this.fullRecordOptions['novelistTab'],
      },
      {
        fullRecordFormControl: 'patronGuestDisplayNovelist',
        fullRecordOption: this.fullRecordOptions['novelistTab'],
      },
      {
        fullRecordFormControl: 'staffDisplayCLCD',
        fullRecordOption: this.fullRecordOptions['clcdTab'],
      },
      {
        fullRecordFormControl: 'patronGuestDisplayCLCD',
        fullRecordOption: this.fullRecordOptions['clcdTab'],
      },
      {
        fullRecordFormControl: 'staffDisplayShelfBrowse',
        fullRecordOption: this.fullRecordOptions['shelfBrowseTab'],
      },
      {
        fullRecordFormControl: 'patronGuestDisplayShelfBrowse',
        fullRecordOption: this.fullRecordOptions['shelfBrowseTab'],
      },
      {
        fullRecordFormControl: 'staffDisplayGoodreadsReview',
        fullRecordOption: this.fullRecordOptions['goodreadsReviewTab'],
      },
      {
        fullRecordFormControl: 'patronGuestDisplayGoodreadsReview',
        fullRecordOption: this.fullRecordOptions['goodreadsReviewTab'],
      },
    ];

    // "fullRecordOption" could be null so remove those.
    formControlOptions = formControlOptions?.filter((f) => f.fullRecordOption);

    this.fullRecordForm = this._formBuilder.group(
      {
        staffDefaultTabId: [this.fullRecordOptions.defaultTabId.staff],
        patronGuestDefaultTabId: [this.fullRecordOptions.defaultTabId.patronGuest],

        staffDetailLabel: [this.fullRecordOptions.detailTab.staffLabel, Validators.required],
        patronGuestDetailLabel: [
          this.fullRecordOptions.detailTab.patronGuestLabel,
          Validators.required,
        ],

        staffWhereLabel: [this.fullRecordOptions.whereTab.staffLabel, Validators.required],
        patronGuestWhereLabel: [
          this.fullRecordOptions.whereTab.patronGuestLabel,
          Validators.required,
        ],

        staffDisplayMARC: [this.fullRecordOptions.displayMARC.staff],
        patronGuestDisplayMARC: [this.fullRecordOptions.displayMARC.patronGuest],
        staffMARCLabel: [
          {
            value: this.fullRecordOptions.marcTab.staffLabel,
            disabled: !this.fullRecordOptions.displayMARC.staff,
          },
          Validators.required,
        ],
        patronGuestMARCLabel: [
          {
            value: this.fullRecordOptions.marcTab.patronGuestLabel,
            disabled: !this.fullRecordOptions.displayMARC.patronGuest,
          },
          Validators.required,
        ],

        staffDisplayGoodreadsReview: [this.fullRecordOptions.displayGoodreadsReview.staff],
        patronGuestDisplayGoodreadsReview: [
          this.fullRecordOptions.displayGoodreadsReview.patronGuest,
        ],
        staffGoodreadsReviewLabel: [
          { value: this.fullRecordOptions.goodreadsReviewTab.staffLabel, disabled: true },
          Validators.required,
        ],
        patronGuestGoodreadsReviewLabel: [
          { value: this.fullRecordOptions.goodreadsReviewTab.patronGuestLabel, disabled: true },
          Validators.required,
        ],

        staffDisplayMoreAboutThisTitle: [this.fullRecordOptions.displayMoreAboutThisTitle?.staff],
        patronGuestDisplayMoreAboutThisTitle: [
          this.fullRecordOptions.displayMoreAboutThisTitle?.patronGuest,
        ],
        staffMoreAboutThisTitleLabel: [
          {
            value: this.fullRecordOptions.moreAboutThisTitleTab?.staffLabel,
            disabled: !this.fullRecordOptions.displayMoreAboutThisTitle?.staff,
          },
          Validators.required,
        ],
        patronGuestMoreAboutThisTitleLabel: [
          {
            value: this.fullRecordOptions.moreAboutThisTitleTab?.patronGuestLabel,
            disabled: !this.fullRecordOptions.displayMoreAboutThisTitle?.patronGuest,
          },
          Validators.required,
        ],

        staffDisplaySyndeticsUnbound: [this.fullRecordOptions.displaySyndeticsUnbound?.staff],
        patronGuestDisplaySyndeticsUnbound: [
          this.fullRecordOptions.displaySyndeticsUnbound?.patronGuest,
        ],
        staffSyndeticsUnboundLabel: [
          {
            value: this.fullRecordOptions.syndeticsUnboundTab?.staffLabel,
            disabled: !this.fullRecordOptions.displaySyndeticsUnbound?.staff,
          },
          Validators.required,
        ],
        patronGuestSyndeticsUnboundLabel: [
          {
            value: this.fullRecordOptions.syndeticsUnboundTab?.patronGuestLabel,
            disabled: !this.fullRecordOptions.displaySyndeticsUnbound?.patronGuest,
          },
          Validators.required,
        ],

        staffDisplayNovelist: [this.fullRecordOptions.displayNovelist?.staff],
        patronGuestDisplayNovelist: [this.fullRecordOptions.displayNovelist?.patronGuest],
        staffNovelistLabel: [
          {
            value: this.fullRecordOptions.novelistTab?.staffLabel,
            disabled: !this.fullRecordOptions.displayNovelist?.staff,
          },
          Validators.required,
        ],
        patronGuestNovelistLabel: [
          {
            value: this.fullRecordOptions.novelistTab?.patronGuestLabel,
            disabled: !this.fullRecordOptions.displayNovelist?.patronGuest,
          },
          Validators.required,
        ],

        staffDisplayCLCD: [this.fullRecordOptions.displayCLCD?.staff],
        patronGuestDisplayCLCD: [this.fullRecordOptions.displayCLCD?.patronGuest],
        staffCLCDLabel: [
          {
            value: this.fullRecordOptions.clcdTab?.staffLabel,
            disabled: !this.fullRecordOptions.displayCLCD?.staff,
          },
          Validators.required,
        ],
        patronGuestCLCDLabel: [
          {
            value: this.fullRecordOptions.clcdTab?.patronGuestLabel,
            disabled: !this.fullRecordOptions.displayCLCD?.patronGuest,
          },
          Validators.required,
        ],

        staffDisplayShelfBrowse: [this.fullRecordOptions.displayShelfBrowse?.staff],
        patronGuestDisplayShelfBrowse: [this.fullRecordOptions.displayShelfBrowse?.patronGuest],
        staffShelfBrowseLabel: [
          {
            value: this.fullRecordOptions.shelfBrowseTab?.staffLabel,
            disabled: !this.fullRecordOptions.displayShelfBrowse?.staff,
          },
          Validators.required,
        ],
        patronGuestShelfBrowseLabel: [
          {
            value: this.fullRecordOptions.shelfBrowseTab?.patronGuestLabel,
            disabled: !this.fullRecordOptions.displayShelfBrowse?.patronGuest,
          },
          Validators.required,
        ],

        lenderColumnLabel: [
          this.fullRecordOptions.lenderColumnLabel
            ? this.fullRecordOptions.lenderColumnLabel
            : 'ILL Lender?',
          Validators.required,
        ],
        lenderContentsLabel: [
          this.fullRecordOptions.lenderContentsLabel
            ? this.fullRecordOptions.lenderContentsLabel
            : '[ILL Lender]',
          Validators.required,
        ],
        copyCatalogForStaff: [this.fullRecordOptions.copyCatalogForStaff],
      },
      {
        validators: [selectValidator(formControlOptions)],
      },
    );
  }

  saveFullRecordOptions(formRawValue: any): void {
    // any is datatype in angular documentation
    if (!this.fullRecordForm.valid) {
      return;
    }

    this._mapToRequest(formRawValue);

    this._opacSettingsService.updateFullRecordOptions(this.fullRecordOptions).subscribe(
      (response) => {
        this._sharedService.openSnackBar(response.message, 'ok');
        this.updateFullRecord.emit(this.fullRecordOptions);
      },
      () => {
        this._sharedService.openSnackBar('Error Saving Full Record Tab Configurations', 'ok');
      },
    );
  }

  private _mapToRequest(formRawValue: any) {
    this.fullRecordOptions.defaultTabId.staff = formRawValue['staffDefaultTabId'];
    this.fullRecordOptions.defaultTabId.patronGuest = formRawValue['patronGuestDefaultTabId'];
    this.fullRecordOptions.displayMARC.staff = formRawValue['staffDisplayMARC'];
    this.fullRecordOptions.displayMARC.patronGuest = formRawValue['patronGuestDisplayMARC'];
    this.fullRecordOptions.displayGoodreadsReview.staff =
      formRawValue['staffDisplayGoodreadsReview'];
    this.fullRecordOptions.displayGoodreadsReview.patronGuest =
      formRawValue['patronGuestDisplayGoodreadsReview'];
    this.fullRecordOptions.detailTab.staffLabel = formRawValue['staffDetailLabel'];
    this.fullRecordOptions.detailTab.patronGuestLabel = formRawValue['patronGuestDetailLabel'];
    this.fullRecordOptions.whereTab.staffLabel = formRawValue['staffWhereLabel'];
    this.fullRecordOptions.whereTab.patronGuestLabel = formRawValue['patronGuestWhereLabel'];
    this.fullRecordOptions.goodreadsReviewTab.staffLabel =
      formRawValue['staffGoodreadsReviewLabel'];
    this.fullRecordOptions.goodreadsReviewTab.patronGuestLabel =
      formRawValue['patronGuestGoodreadsReviewLabel'];
    this.fullRecordOptions.marcTab.staffLabel = formRawValue['staffMARCLabel'];
    this.fullRecordOptions.marcTab.patronGuestLabel = formRawValue['patronGuestMARCLabel'];
    this.fullRecordOptions.copyCatalogForStaff = formRawValue['copyCatalogForStaff'];
    this.fullRecordOptions.lenderContentsLabel = formRawValue['lenderContentsLabel'];
    this.fullRecordOptions.lenderColumnLabel = formRawValue['lenderColumnLabel'];

    if (
      this.fullRecordOptions.displayMoreAboutThisTitle &&
      this.fullRecordOptions.moreAboutThisTitleTab
    ) {
      this.fullRecordOptions.displayMoreAboutThisTitle.staff =
        formRawValue['staffDisplayMoreAboutThisTitle'];
      this.fullRecordOptions.displayMoreAboutThisTitle.patronGuest =
        formRawValue['patronGuestDisplayMoreAboutThisTitle'];
      this.fullRecordOptions.moreAboutThisTitleTab.staffLabel =
        formRawValue['staffMoreAboutThisTitleLabel'];
      this.fullRecordOptions.moreAboutThisTitleTab.patronGuestLabel =
        formRawValue['patronGuestMoreAboutThisTitleLabel'];
    }

    if (
      this.fullRecordOptions.displaySyndeticsUnbound &&
      this.fullRecordOptions.syndeticsUnboundTab
    ) {
      this.fullRecordOptions.displaySyndeticsUnbound.staff =
        formRawValue['staffDisplaySyndeticsUnbound'];
      this.fullRecordOptions.displaySyndeticsUnbound.patronGuest =
        formRawValue['patronGuestDisplaySyndeticsUnbound'];
      this.fullRecordOptions.syndeticsUnboundTab.staffLabel =
        formRawValue['staffSyndeticsUnboundLabel'];
      this.fullRecordOptions.syndeticsUnboundTab.patronGuestLabel =
        formRawValue['patronGuestSyndeticsUnboundLabel'];
    }

    if (this.fullRecordOptions.displayNovelist && this.fullRecordOptions.novelistTab) {
      this.fullRecordOptions.displayNovelist.staff = formRawValue['staffDisplayNovelist'];
      this.fullRecordOptions.displayNovelist.patronGuest =
        formRawValue['patronGuestDisplayNovelist'];
      this.fullRecordOptions.novelistTab.staffLabel = formRawValue['staffNovelistLabel'];
      this.fullRecordOptions.novelistTab.patronGuestLabel =
        formRawValue['patronGuestNovelistLabel'];
    }

    if (this.fullRecordOptions.displayCLCD && this.fullRecordOptions.clcdTab) {
      this.fullRecordOptions.displayCLCD.staff = formRawValue['staffDisplayCLCD'];
      this.fullRecordOptions.displayCLCD.patronGuest = formRawValue['patronGuestDisplayCLCD'];
      this.fullRecordOptions.clcdTab.staffLabel = formRawValue['staffCLCDLabel'];
      this.fullRecordOptions.clcdTab.patronGuestLabel = formRawValue['patronGuestCLCDLabel'];
    }

    if (this.fullRecordOptions.displayShelfBrowse && this.fullRecordOptions.shelfBrowseTab) {
      this.fullRecordOptions.displayShelfBrowse.staff = formRawValue['staffDisplayShelfBrowse'];
      this.fullRecordOptions.displayShelfBrowse.patronGuest =
        formRawValue['patronGuestDisplayShelfBrowse'];
      this.fullRecordOptions.shelfBrowseTab.staffLabel = formRawValue['staffShelfBrowseLabel'];
      this.fullRecordOptions.shelfBrowseTab.patronGuestLabel =
        formRawValue['patronGuestShelfBrowseLabel'];
    }
  }

  toggleFormControl(label: string): void {
    this.fullRecordForm.controls[label].disabled
      ? this.fullRecordForm.controls[label].enable()
      : this.fullRecordForm.controls[label].disable();
  }
}
